/* eslint-disable no-redeclare */
/* eslint-disable no-unused-vars */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactModal from 'react-modal';
import { RegularButton, ICommonDropdownOption, AlertAdapter, CommonDropdown, FullScreenLoading } from 'pg-ui-library';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import closeSvg from 'src/assets/close.svg';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import UserService from 'src/services/user.service';
import { AxiosResponse } from 'axios';
import { UserAdditionBody, UserAdditionForm, UserAdditionHeader, InputContainer } from './styles';

import { usePromoteuser } from '../../mutations/user-setting.mutation';

interface AllUsers {
  key: string;
  id: string;
  email: string;
}

export interface User {
  id: string;
  email: string;
}

interface AddUsersProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onRequestClose: () => void;
  onSuccess?: () => void;
}

const AddUsers: React.FC<AddUsersProps> = ({ isOpen, setIsOpen, onRequestClose, onSuccess }) => {
  const { t } = useTranslation();
  const [searchUsers, setSearchUsers] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [dropdownOptionsUsers, setDropdownOptionsUsers] = useState<ICommonDropdownOption[]>([]);
  const promoteuser = usePromoteuser();

  const usersSearch = useQuery<AxiosResponse<{ items: AllUsers[] }>>(['/admin/users/search'], async () => {
    try {
      const result = await UserService.getUsersSearchNew(searchUsers);
      return result;
    } catch (e) {
      const errorMessage = 'Error While Fetching the users';
      AlertAdapter(errorMessage, 'error');
      throw new Error(errorMessage);
    }
  });

  const validationSchema = yup.object({
    email: yup.string().required(),
  });

  const formik = useFormik({
    initialValues: {
      userId: selectedUserId,
      email: selectedUser,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        await promoteuser.mutateAsync({ promoteUser: values });
        AlertAdapter('User has been added to admin!', 'success');
        setIsOpen(false);
        if (onSuccess) onSuccess();
        onRequestClose();
      } catch (error: any) {
        const errorMsg = error?.toString() || 'An error occurred while promoting the user';
        AlertAdapter(errorMsg, 'error');
        onRequestClose();
      }
    },
  });

  const renderDropdownOptions = () => {
    if (usersSearch.isLoading) {
      return <FullScreenLoading />;
    }

    if (usersSearch.error) {
      return <p>Error fetching users</p>;
    }

    const options =
      usersSearch.data?.data.items.map((user: User) => ({
        key: user.id,
        label: user.email,
        value: user.email,
      })) || [];

    return (
      <CommonDropdown
        id="Email"
        name="Email"
        label="Email"
        options={[{ key: '', label: 'Select Email', value: '' }, ...options]}
        value={formik.values.email}
        setValue={(value: string) => {
          const selectedUsers = usersSearch.data?.data.items.find(user => user.email === value);
          if (selectedUsers) {
            setSelectedUser(value);
            setSelectedUserId(selectedUsers.id);
            formik.setFieldValue('email', value);
            formik.setFieldValue('userId', selectedUsers.id);
          }
        }}
        style={{ minWidth: '100%' }}
      />
    );
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '40%',
          height: '285px',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 20px 0 20px',
          background: '#F3F4F8',
          overflow: 'hidden',
        },
      }}
    >
      <div>
        <UserAdditionHeader>
          <div>
            <h3 style={{ marginBottom: '-8px', marginLeft: '-9px' }}>Add Admin User</h3>
          </div>
          <CloseIcon src={closeSvg} onClick={onRequestClose} />
        </UserAdditionHeader>
        <UserAdditionForm onSubmit={formik.handleSubmit}>
          <UserAdditionBody>
            <InputContainer>
              {renderDropdownOptions()}
              <RegularButton
                onClick={() => formik.handleSubmit()}
                style={{ marginTop: '35px', marginLeft: '87%' }}
                disabled={formik.isSubmitting || selectedUser === ''}
              >
                Save
              </RegularButton>
            </InputContainer>
          </UserAdditionBody>
        </UserAdditionForm>
      </div>
    </ReactModal>
  );
};

AddUsers.defaultProps = {
  onSuccess: undefined,
};

export default AddUsers;
